import type { RouteRecordRaw } from 'vue-router'
import { getOrderTypeConfig, orderKinds } from '@/config/order-types'
import { getProductTypeConfig, productKinds } from '@/config/product-types'
import type { OrderKind } from '@/types/order'
import type { ProductKind } from '@/types/product'

function productModuleRoutes(kind: ProductKind): RouteRecordRaw[] {
  const config = getProductTypeConfig(kind)
  const listPath = config.listRoute.slice(1)
  const createPath = config.createRoute.slice(1)
  // editRoute 只依赖 id，用占位符取得并保持旧系统的 URL 结构。
  const editPath = config.editRoute(':id').slice(1)
  const formComponent =
    kind === 'course'
      ? () => import('@/views/products/CourseProductFormView.vue')
      : () => import('@/views/products/ProductFormView.vue')
  return [
    {
      path: listPath,
      component: () => import('@/views/products/ProductListView.vue'),
      meta: {
        title: config.title,
        permission: config.permission,
        menuKey: config.listRoute,
        productKind: kind,
      },
    },
    {
      path: createPath,
      component: formComponent,
      meta: {
        title: `新增${config.title}`,
        permission: config.permission,
        menuKey: config.listRoute,
        productKind: kind,
      },
    },
    {
      path: editPath,
      component: formComponent,
      meta: {
        title: `编辑${config.title}`,
        permission: config.permission,
        menuKey: config.listRoute,
        productKind: kind,
      },
    },
  ]
}

function orderModuleRoutes(kind: OrderKind): RouteRecordRaw[] {
  const config = getOrderTypeConfig(kind)
  const root = config.routePrefix.slice(1)
  const permission = `order:${kind}`
  const component = () => import('@/views/orders/OrderDetailView.vue')
  return [
    {
      path: `${root}/list`,
      component: () => import('@/views/orders/OrderListView.vue'),
      meta: {
        title: config.title,
        permission,
        menuKey: `${config.routePrefix}/list`,
        orderKind: kind,
      },
    },
    {
      path: `${root}/info/:id`,
      component,
      meta: {
        title: `${config.title}详情`,
        permission,
        menuKey: `${config.routePrefix}/list`,
        orderKind: kind,
      },
    },
    {
      path: `${root}/edit/:id`,
      component,
      meta: {
        title: `编辑${config.title}`,
        permission,
        menuKey: `${config.routePrefix}/list`,
        orderKind: kind,
      },
    },
  ]
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: '登录', public: true },
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/errors/ForbiddenView.vue'),
    meta: { title: '无权访问', public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: { title: '首页' },
      },
      ...productKinds.flatMap(productModuleRoutes),
      {
        path: 'product/zhongyingGoods/list',
        component: () => import('@/views/products/ZhongyingListView.vue'),
        meta: {
          title: '自营商品',
          permission: 'product:zhongying',
          menuKey: '/product/zhongyingGoods/list',
        },
      },
      {
        path: 'product/zhongyingGoods/detail/:id',
        component: () => import('@/views/products/ZhongyingFormView.vue'),
        props: { readonly: true },
        meta: {
          title: '自营商品详情',
          permission: 'product:zhongying',
          menuKey: '/product/zhongyingGoods/list',
        },
      },
      {
        path: 'product/zhongyingGoods/edit/:id',
        component: () => import('@/views/products/ZhongyingFormView.vue'),
        meta: {
          title: '编辑自营商品',
          permission: 'product:zhongying',
          menuKey: '/product/zhongyingGoods/list',
        },
      },
      ...orderKinds.flatMap(orderModuleRoutes),
      {
        path: 'order/after-sales',
        component: () => import('@/views/orders/AfterSaleListView.vue'),
        meta: { title: '售后管理', permission: 'order:after-sales', menuKey: '/order/after-sales' },
      },
      {
        path: 'order/after-sales/:id',
        name: 'after-sale-detail',
        component: () => import('@/views/orders/AfterSaleDetailView.vue'),
        meta: { title: '售后详情', permission: 'order:after-sales', menuKey: '/order/after-sales' },
      },
      {
        path: 'assets/withdrawList',
        component: () => import('@/views/assets/WithdrawListView.vue'),
        meta: { title: '资金提现', permission: 'assets:read', menuKey: '/assets/withdrawList' },
      },
      {
        path: 'assets/withdraw',
        component: () => import('@/views/assets/WithdrawApplyView.vue'),
        meta: { title: '申请资金提现', permission: 'assets:read', menuKey: '/assets/withdrawList' },
      },
      {
        path: 'assets/capital',
        component: () => import('@/views/assets/CapitalListView.vue'),
        meta: { title: '资金记录', permission: 'assets:read', menuKey: '/assets/capital' },
      },
      {
        path: 'help',
        component: () => import('@/views/help/HelpView.vue'),
        meta: { title: '帮助中心' },
      },
      {
        path: 'setting/password',
        name: 'password',
        component: () => import('@/views/auth/PasswordView.vue'),
        meta: { title: '修改密码', permission: 'settings:password', menuKey: '/setting/password' },
      },
    ],
  },
  {
    path: '/feedback/cash/:type',
    component: () => import('@/views/feedback/CashFeedbackView.vue'),
    meta: { title: '提现结果', public: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
    meta: { title: '页面不存在', public: true },
  },
]
